import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, IconButton, CircularProgress, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button,
  FormControlLabel, Switch, MenuItem,
} from '@mui/material';
import { Home, Add, Edit, Delete, Place, WarningAmber } from '@mui/icons-material';
import axios from 'axios';
import Toast from './Toast';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';

// A bus "Stage" (Kenyan term for a bus stop) — stored as a TransportLocation to
// avoid colliding with the academic Stage model.
const empty = { code: '', name: '', area: '', monthlyFee: 1000, latitude: '', longitude: '', routeCode: '', active: true };
const API = process.env.REACT_APP_API_URL;

export default function BusStageList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [delDlg, setDelDlg] = useState(null);
  const { attemptClose, discardDialog } = useUnsavedGuard({
    open: dialogOpen, value: form, onClose: () => setDialogOpen(false),
    message: 'You have unsaved bus stage details. Close without saving?',
  });

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/api/fleet/locations`, { headers }),
        axios.get(`${API}/api/fleet/routes`, { headers }),
      ]);
      setRows(a.data); setRoutes(b.data); setError('');
    } catch (err) { setError(err.response?.data?.error || 'Failed to load bus stages'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const openNew = () => { setEditing(null); setForm(empty); setDialogOpen(true); };
  const openEdit = (r) => {
    setEditing(r.code);
    setForm({ ...empty, ...r, latitude: r.latitude ?? '', longitude: r.longitude ?? '' });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!editing && !form.code.trim()) return setError('Code is required');
    if (!form.name.trim()) return setError('Name is required');
    if (Number(form.monthlyFee) < 1000) return setError('Monthly fee must be at least KES 1,000');
    const payload = {
      ...form,
      monthlyFee: Number(form.monthlyFee),
      latitude: form.latitude === '' ? null : Number(form.latitude),
      longitude: form.longitude === '' ? null : Number(form.longitude),
    };
    try {
      if (editing) await axios.put(`${API}/api/fleet/locations/${editing}`, payload, { headers });
      else await axios.post(`${API}/api/fleet/locations`, payload, { headers });
      setDialogOpen(false); setError(''); setSuccess(`Bus stage ${editing ? 'updated' : 'created'}`); load();
    } catch (err) { setError(err.response?.data?.error || 'Failed to save'); }
  };

  const remove = async () => {
    try { await axios.delete(`${API}/api/fleet/locations/${delDlg}`, { headers }); setDelDlg(null); setSuccess('Bus stage deleted'); load(); }
    catch (err) { setDelDlg(null); setError(err.response?.data?.error || 'Failed to delete'); }
  };

  const f = (label, key, extra = {}) => (
    <TextField label={label} value={form[key]}
      onChange={e => setForm({ ...form, [key]: e.target.value })}
      fullWidth size="small" {...extra} />
  );

  if (loading) return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Loading bus stages...</Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <Place sx={{ mr: 1, color: '#0277bd' }} />
              <Typography variant="h5" sx={{ color: '#0277bd' }}>Bus Stages</Typography>
            </Box>
            <Box>
              <IconButton onClick={() => navigate('/fleet')} title="Fleet Dashboard" sx={{ mr: 1 }}><Home /></IconButton>
              <IconButton onClick={openNew} title="Add Bus Stage"><Add /></IconButton>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" mb={2}>
            A "stage" is a bus stop. Each carries a monthly fee (min KES 1,000) and optional GPS coordinates to guide the driver &amp; assistant.
          </Typography>
          <Toast error={error} success={success} onClose={(k) => k === 'error' ? setError('') : setSuccess('')} />

          {rows.length === 0 ? (
            <Typography color="text.secondary">No bus stages yet.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Code</strong></TableCell>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Area</strong></TableCell>
                  <TableCell align="right"><strong>Monthly Fee</strong></TableCell>
                  <TableCell><strong>Route</strong></TableCell>
                  <TableCell><strong>Coordinates</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.code} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.code}</TableCell>
                    <TableCell>{r.name}</TableCell>
                    <TableCell>{r.area}</TableCell>
                    <TableCell align="right">{Number(r.monthlyFee).toLocaleString()}</TableCell>
                    <TableCell>{r.routeCode}</TableCell>
                    <TableCell>{r.latitude != null ? `${r.latitude}, ${r.longitude}` : '—'}</TableCell>
                    <TableCell><Chip label={r.active === false ? 'Inactive' : 'Active'} color={r.active === false ? 'default' : 'success'} size="small" variant="outlined" /></TableCell>
                    <TableCell>
                      <IconButton color="primary" size="small" onClick={() => openEdit(r)}><Edit fontSize="small" /></IconButton>
                      <IconButton color="warning" size="small" onClick={() => setDelDlg(r.code)}><Delete fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(delDlg)} onClose={() => setDelDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main' }}>
          <WarningAmber color="warning" /> Delete Bus Stage
        </DialogTitle>
        <DialogContent><Typography>Delete <strong>{delDlg}</strong>?</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDelDlg(null)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={remove}>Delete</Button>
        </DialogActions>
      </Dialog>

      {discardDialog}
      <Dialog open={dialogOpen} onClose={attemptClose} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? `Edit Bus Stage: ${editing}` : 'New Bus Stage'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {f('Code', 'code', { disabled: !!editing })}
            {f('Name', 'name')}
            {f('Area', 'area')}
            {f('Monthly Fee (KES)', 'monthlyFee', { type: 'number', inputProps: { min: 1000 }, helperText: 'Minimum KES 1,000' })}
            <TextField select label="Route" value={form.routeCode} size="small" fullWidth
              onChange={e => setForm({ ...form, routeCode: e.target.value })}>
              <MenuItem value="">(none)</MenuItem>
              {routes.map(rt => <MenuItem key={rt.routeCode} value={rt.routeCode}>{rt.routeCode} — {rt.name}</MenuItem>)}
            </TextField>
            <Box display="flex" gap={2}>
              {f('Latitude', 'latitude', { type: 'number' })}
              {f('Longitude', 'longitude', { type: 'number' })}
            </Box>
            {editing && (
              <FormControlLabel control={<Switch checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} />} label="Active" />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={save}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
